import React, { memo } from "react";
import clsx from "clsx";
import { useStyles } from "./ReportQueueAdminTable.styles"
import moment from "moment";
import '../ResponsiveTable/_responsive.table.css';
import { REPORT_QUEUE_STATUS, REPORT_QUEUE_STATUS_CODES } from "../../constants";
import { Button, useTheme } from "@material-ui/core";
import DurationTimer from "./DurationTimer";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";

const getPodColor = (podId, isDark) => {
  if (!podId) return null;
  
  const lightColors = [ //JG did this, it's acceptable.
    '#e3f2fd', '#f3e5f5', '#e8f5e8', '#fff3e0', '#fce4ec',
    '#e0f2f1', '#f1f8e9', '#fff8e1', '#e8eaf6', '#fafafa'
  ];
  
  const darkColors = [  //JG did this, it's acceptable
    '#3b4f7a', '#6b5b95', '#4a5d4a', '#8b6f47', '#7a5a6b',
    '#4a6b5d', '#6b7a4a', '#8b7a47', '#5a5b7a', '#6a6a6a'
  ];
  
  let hash = 0;
  for (let i = 0; i < podId.length; i++) {
    hash = podId.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const colors = isDark ? darkColors : lightColors;
  return colors[Math.abs(hash) % colors.length];
};

const ReportQueueManagementTable = ({ results = [], onCancel, deleting = {} }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const { textConfirmation } = useConfirmationDialog();

  const handleDeletion = async(result) => {
    const statusText = REPORT_QUEUE_STATUS[result?.rqStatusId] ?? "queued";
    let continueDelete = await textConfirmation({
      title: `Are you sure you want to cancel the report?`,
      message: `Please enter the report name EXACTLY as shown in the input box below to cancel. Report is currently ${statusText}.`,
      textConfirmation: result.reportParameters?.reportname,
    });

    if (!continueDelete) return;

    onCancel(result.rqId)
  }

  return (
    <div className={classes.tableContainer}>  
    <table className={clsx(["a1-responsive", classes.reportsQueueAdminTable])}>     
        <thead className={clsx(classes.thHeader)}>
          <tr>
            <th className={clsx(classes.resultHeader)}>Report Name</th>
            <th className={clsx(classes.centerHeader)}>Facility</th>
            <th className={clsx(classes.centerHeader)}>Queued</th>
            <th className={clsx(classes.centerHeader)}>Duration</th>
            <th className={clsx(classes.centerHeader)}>Status</th>
            <th className={clsx(classes.centerHeader)}>Pod</th>
            <th className={clsx(classes.centerHeader)}>Action</th>
          </tr>
        </thead>

        <tbody>
          {results.map((result) =>  {

            return (
            <tr
              data-pod={result?.rqProcessorId ?? "none"}
              key={result.id}
              data-testid="reportqueue-row"
              className={clsx({ "animate__animated animate__fadeOutUp": deleting[result.rqId] })}
            >
              <td
                data-column="Report Name"
              >                           
                <span className={clsx([classes.left])}>{result.reportParameters?.reportname}</span>  
              </td>
              <td
                data-column="Facility"
                className={clsx([classes.centerAlign])}
              >
                {result.reportParameters?.parameters?.validfacility}
              </td>
              <td
                data-column="Queued"
                className={clsx([classes.centerAlign])}
              >
                {result?.rqCreatedTime ?  moment(result.rqCreatedTime).format("MM/DD/YYYY h:mm A") : ""}
              </td>
              <td
                data-column="Duration"
                className={clsx([classes.centerAlign])}
              >
                {result.rqStatusId === REPORT_QUEUE_STATUS_CODES.PROCESSING ? 
                  (
                    <DurationTimer startTime={result.rqPickupTime} />
                  ) : "NA"
                }
              </td>
              <td data-column="Status" 
                className={clsx([
                  classes.centerAlign,
                ])}              
              >
                <span
                className={clsx([classes.status,
                  REPORT_QUEUE_STATUS[result?.rqStatusId]?.toLowerCase().replace(/\s+/g, "-")
                ])}
                >
                  { 
                    result?.rqStatusId
                    ? REPORT_QUEUE_STATUS[result.rqStatusId]
                    : "Queued"
                  }                  
                </span>
              </td>
              <td
                data-column="Pod"
                className={clsx([classes.centerAlign])}
              >
                <div className={clsx([classes.centerAlign, classes.podChip])}
                     style={result?.rqProcessorId ? { backgroundColor: getPodColor(result.rqProcessorId, isDark) } : {}}
                >
                  {result?.rqProcessorId ?? "N/A"}
                </div>
              </td>
              <td
                data-column="Action"
                className={clsx([classes.centerAlign])}
              >
                <Button
                  variant="contained" 
                  color="primary" 
                  size="small" 
                  onClick={() => handleDeletion(result)}>
                    Cancel
                </Button>
              </td>     
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default memo(ReportQueueManagementTable);
